import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, RefreshCw, Power, Boxes, Save, Copy, Check } from 'lucide-react';
import {
  miniAppsApi,
  type PublicMiniApp,
  type MiniAppVisibility,
  type CreateMiniAppInput,
} from '@/api/miniApps';

const ALL_CAPABILITIES = [
  'user.read',
  'theme.read',
  'context.read',
  'chat.sendMessage',
  'ui.close',
  'ui.toast',
  'ui.confirm',
];

/**
 * `/miniapps/manage` — управление мини-аппами владельца.
 *
 * Подход тот же, что у `/bots`: список слева + редактор справа. Сырой
 * signing secret показывается ровно один раз — после create / rotate-secret.
 */
export default function MiniAppsManagePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [issuedSecret, setIssuedSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: apps = [], isLoading } = useQuery({
    queryKey: ['miniapps', 'owned'],
    queryFn: () => miniAppsApi.listOwned(),
  });

  const selected = apps.find((a) => a.id === selectedId) ?? null;

  const removeMutation = useMutation({
    mutationFn: (id: string) => miniAppsApi.remove(id),
    onSuccess: () => {
      setSelectedId(null);
      qc.invalidateQueries({ queryKey: ['miniapps', 'owned'] });
    },
  });

  const rotateMutation = useMutation({
    mutationFn: (id: string) => miniAppsApi.rotateSecret(id),
    onSuccess: (r) => setIssuedSecret(r.signingSecret),
  });

  return (
    <div className="flex-1 flex flex-col bg-surface-950 text-surface-100">
      <div className="h-12 border-b border-surface-800 px-4 flex items-center gap-3">
        <button onClick={() => navigate('/miniapps')} className="btn-ghost p-1.5 text-surface-400">
          <ArrowLeft size={20} />
        </button>
        <Boxes size={18} className="text-brand-400" />
        <span className="font-semibold">Мои мини-аппы</span>
        <button
          onClick={() => setCreating(true)}
          className="ml-auto px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-500 text-white text-sm flex items-center gap-1"
        >
          <Plus size={14} /> Новый
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <aside className="w-64 border-r border-surface-800 overflow-y-auto">
          {isLoading && <div className="p-4 text-surface-500 text-sm">Загрузка…</div>}
          {!isLoading && apps.length === 0 && (
            <div className="p-4 text-surface-500 text-sm">У вас пока нет мини-аппов</div>
          )}
          {apps.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelectedId(a.id)}
              className={`w-full text-left px-4 py-3 border-b border-surface-900 hover:bg-surface-900 ${
                selectedId === a.id ? 'bg-surface-900' : ''
              }`}
            >
              <div className="text-sm font-semibold truncate">{a.displayName}</div>
              <div className="text-xs text-surface-500 font-mono truncate">@{a.slug}</div>
              <div className="text-[10px] text-surface-600 mt-0.5 flex items-center gap-2">
                {a.visibility} {!a.isEnabled && <span className="text-amber-400">disabled</span>}
              </div>
            </button>
          ))}
        </aside>

        <section className="flex-1 overflow-y-auto p-4">
          {!selected && !creating && (
            <div className="text-surface-500 text-sm">Выберите мини-апп слева или создайте новый</div>
          )}
          {creating && (
            <CreateForm
              onCancel={() => setCreating(false)}
              onCreated={(secret, id) => {
                setCreating(false);
                setIssuedSecret(secret);
                setSelectedId(id);
                qc.invalidateQueries({ queryKey: ['miniapps', 'owned'] });
              }}
            />
          )}
          {selected && !creating && (
            <EditForm
              app={selected}
              onUpdated={() => qc.invalidateQueries({ queryKey: ['miniapps', 'owned'] })}
              onDelete={() => {
                if (window.confirm(`Удалить мини-апп «${selected.displayName}»?`)) {
                  removeMutation.mutate(selected.id);
                }
              }}
              onRotateSecret={() => {
                if (window.confirm('Сгенерировать новый signing secret? Старый будет инвалидирован.')) {
                  rotateMutation.mutate(selected.id);
                }
              }}
            />
          )}
        </section>
      </div>

      {issuedSecret && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-surface-900 border border-surface-700 rounded-xl p-5 max-w-lg w-full">
            <h3 className="font-semibold text-surface-100 mb-2">Signing secret</h3>
            <p className="text-xs text-surface-400 mb-3">
              Сохраните этот секрет — он используется на стороне мини-апа для
              верификации webhook'ов от IRChat. Мы больше не покажем его в открытом виде.
            </p>
            <div className="flex items-center gap-2 bg-surface-950 border border-surface-800 rounded-md p-2">
              <code className="flex-1 text-xs text-brand-300 font-mono break-all">{issuedSecret}</code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(issuedSecret);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                className="p-1.5 text-surface-400 hover:text-surface-100"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
            <button
              onClick={() => setIssuedSecret(null)}
              className="mt-4 px-3 py-1.5 bg-brand-600 hover:bg-brand-500 text-white rounded-md text-sm"
            >
              Я сохранил
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (signingSecret: string, id: string) => void;
}) {
  const [form, setForm] = useState<CreateMiniAppInput>({
    slug: '',
    displayName: '',
    url: '',
    allowedOrigins: [],
    capabilities: ['user.read', 'context.read', 'theme.read'],
    visibility: 'PRIVATE',
  });
  const [originsRaw, setOriginsRaw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (input: CreateMiniAppInput) => miniAppsApi.create(input),
    onSuccess: (res) => onCreated(res.signingSecret, res.id),
    onError: (err: { response?: { data?: { message?: string } } }) =>
      setError(err?.response?.data?.message ?? 'Ошибка'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        const allowedOrigins = originsRaw
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean);
        mutation.mutate({ ...form, allowedOrigins });
      }}
      className="max-w-xl space-y-3"
    >
      <h2 className="text-lg font-semibold">Новый мини-апп</h2>
      <Field label="Slug (a-z0-9_-, 3..32)">
        <input
          required
          value={form.slug}
          onChange={(e) => setForm({ ...form, slug: e.target.value })}
          className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm text-surface-100"
        />
      </Field>
      <Field label="Название">
        <input
          required
          value={form.displayName}
          onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm text-surface-100"
        />
      </Field>
      <Field label="URL мини-апа (HTTPS)">
        <input
          required
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
          className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm text-surface-100"
          placeholder="https://apps.example.com/poll"
        />
      </Field>
      <Field label="Allowed origins (через запятую)">
        <input
          required
          value={originsRaw}
          onChange={(e) => setOriginsRaw(e.target.value)}
          className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm text-surface-100"
          placeholder="https://apps.example.com"
        />
      </Field>
      <Field label="Capabilities">
        <div className="grid grid-cols-2 gap-1">
          {ALL_CAPABILITIES.map((c) => (
            <label key={c} className="flex items-center gap-2 text-xs text-surface-300">
              <input
                type="checkbox"
                checked={form.capabilities.includes(c)}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...form.capabilities, c]
                    : form.capabilities.filter((x) => x !== c);
                  setForm({ ...form, capabilities: next });
                }}
              />
              <code>{c}</code>
            </label>
          ))}
        </div>
      </Field>
      <Field label="Видимость">
        <select
          value={form.visibility}
          onChange={(e) => setForm({ ...form, visibility: e.target.value as MiniAppVisibility })}
          className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm text-surface-100"
        >
          <option value="PRIVATE">PRIVATE — только владелец</option>
          <option value="UNLISTED">UNLISTED — по прямой ссылке</option>
          <option value="PUBLIC">PUBLIC — в каталоге</option>
        </select>
      </Field>
      {error && <div className="text-xs text-red-400">{error}</div>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="px-3 py-1.5 bg-brand-600 hover:bg-brand-500 text-white rounded-md text-sm flex items-center gap-1"
        >
          <Save size={14} /> Создать
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 bg-surface-800 hover:bg-surface-700 text-surface-200 rounded-md text-sm"
        >
          Отмена
        </button>
      </div>
    </form>
  );
}

function EditForm({
  app,
  onUpdated,
  onDelete,
  onRotateSecret,
}: {
  app: PublicMiniApp;
  onUpdated: () => void;
  onDelete: () => void;
  onRotateSecret: () => void;
}) {
  const [displayName, setDisplayName] = useState(app.displayName);
  const [url, setUrl] = useState(app.url);
  const [originsRaw, setOriginsRaw] = useState(app.allowedOrigins.join(', '));
  const [capabilities, setCapabilities] = useState<string[]>(app.capabilities);
  const [visibility, setVisibility] = useState<MiniAppVisibility>(app.visibility);
  const [isEnabled, setIsEnabled] = useState(app.isEnabled);
  const [shortDescription, setShortDescription] = useState(app.shortDescription ?? '');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      miniAppsApi.update(app.id, {
        displayName,
        url,
        allowedOrigins: originsRaw
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        capabilities,
        visibility,
        isEnabled,
        shortDescription: shortDescription || null,
      }),
    onSuccess: () => onUpdated(),
    onError: (err: { response?: { data?: { message?: string } } }) =>
      setError(err?.response?.data?.message ?? 'Ошибка'),
  });

  return (
    <div className="max-w-xl space-y-3">
      <div className="flex items-start gap-2">
        <h2 className="text-lg font-semibold flex-1 truncate">{app.displayName}</h2>
        <button
          onClick={onRotateSecret}
          className="px-2 py-1 bg-surface-800 hover:bg-surface-700 rounded-md text-xs flex items-center gap-1 text-surface-200"
        >
          <RefreshCw size={12} /> rotate secret
        </button>
        <button
          onClick={onDelete}
          className="px-2 py-1 bg-red-900/40 hover:bg-red-900/60 rounded-md text-xs flex items-center gap-1 text-red-200"
        >
          <Trash2 size={12} /> delete
        </button>
      </div>

      <Field label="Название">
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm text-surface-100" />
      </Field>
      <Field label="Краткое описание">
        <input
          value={shortDescription}
          onChange={(e) => setShortDescription(e.target.value)}
          className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm text-surface-100"
        />
      </Field>
      <Field label="URL">
        <input value={url} onChange={(e) => setUrl(e.target.value)} className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm text-surface-100" />
      </Field>
      <Field label="Allowed origins">
        <input value={originsRaw} onChange={(e) => setOriginsRaw(e.target.value)} className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm text-surface-100" />
      </Field>
      <Field label="Capabilities">
        <div className="grid grid-cols-2 gap-1">
          {ALL_CAPABILITIES.map((c) => (
            <label key={c} className="flex items-center gap-2 text-xs text-surface-300">
              <input
                type="checkbox"
                checked={capabilities.includes(c)}
                onChange={(e) =>
                  setCapabilities((prev) =>
                    e.target.checked ? [...prev, c] : prev.filter((x) => x !== c),
                  )
                }
              />
              <code>{c}</code>
            </label>
          ))}
        </div>
      </Field>
      <Field label="Видимость">
        <select
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as MiniAppVisibility)}
          className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm text-surface-100"
        >
          <option value="PRIVATE">PRIVATE</option>
          <option value="UNLISTED">UNLISTED</option>
          <option value="PUBLIC">PUBLIC</option>
        </select>
      </Field>
      <label className="flex items-center gap-2 text-sm text-surface-200">
        <input type="checkbox" checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
        <Power size={14} /> Включён
      </label>
      {error && <div className="text-xs text-red-400">{error}</div>}
      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="px-3 py-1.5 bg-brand-600 hover:bg-brand-500 text-white rounded-md text-sm flex items-center gap-1"
      >
        <Save size={14} /> Сохранить
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-surface-400 mb-1">{label}</span>
      {children}
    </label>
  );
}
